import { ItemType } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';
import {
  showZendeskArticle,
  zendeskArticleIds,
} from 'shared/utils/zendesk/zendeskUtils';

type NotQualifiedScreenProps = {
  data?: {
    vlocNotQualified?: {
      title?: string | null;
      subTitle?: string | null;
      description?: React.ReactNode;
      optionItems?: string[];
      footer?: React.ReactNode;
    };
    vlocNoBelieve?: {
      title?: string | null;
      subTitle?: string | null;
      description?: React.ReactNode;
      articleLink?: {
        id?: string;
        copy?: string;
      };
    } | null;
  };
};

const NotQualifiedScreen = ({ data }: NotQualifiedScreenProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');
  const { bottom } = useSafeAreaInsets();

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_TRACKER_CARD_TILE_CREDIT_TRACKER,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const vlocNotQualified = data?.vlocNotQualified ?? null;
  const vlocNoBelieve = data?.vlocNoBelieve ?? null;
  const zendeskArticleId = vlocNoBelieve?.articleLink?.id;

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    goHomeEvent();
    dismissToTab(Tabs.HOME);
  };

  const goBelieve = () => {
    router.navigate('/believe/apply');
  };

  const showZendesk = () => {
    try {
      showZendeskArticle(
        zendeskArticleId
          ? parseInt(zendeskArticleId)
          : zendeskArticleIds.APPLYING_FOR_BELIEVE,
      );
    } catch (error) {
      console.error('Error showing Zendesk article:', error);
    }
  };

  const goHomeEvent = usePressableAnalytics({
    analyticsId: 'vloc_not_qualified_goToHomeClicked',
    customAttributes: { cta: 'Done' },
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: vlocNoBelieve
      ? '/varo-line-of-credit/not-qualified/withBelieve'
      : '/varo-line-of-credit/not-qualified/withoutBelieve',
  });

  useBackHandler(goHome);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          gestureEnabled: false,
          headerTransparent: false,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              primaryIcon={
                <Icon iconKey="CLOSE" iconAccessibilityLabel="Close" />
              }
              primaryOnPress={goHome}
              {...props}
            />
          ),
        }}
      />
      <View style={styles.contentWrapper}>
        <ScrollView contentContainerStyle={styles.scrollViewContainer}>
          <View style={styles.mainContent}>
            {vlocNotQualified?.title ? (
              <TitleLgText style={styles.enhancedStyle} numberOfLines={0}>
                {vlocNotQualified.title}
              </TitleLgText>
            ) : null}

            {vlocNotQualified?.subTitle ? (
              <BodyBaseBoldText style={styles.defaultStyle} numberOfLines={0}>
                {vlocNotQualified.subTitle}
              </BodyBaseBoldText>
            ) : null}

            {vlocNotQualified?.description ? (
              <BodyBaseText style={styles.defaultStyle} numberOfLines={0}>
                {vlocNotQualified.description}
              </BodyBaseText>
            ) : null}

            {vlocNotQualified?.optionItems ? (
              <View style={styles.enhancedStyle}>
                <ContentList
                  itemType={ItemType.Bullet}
                  contentList={vlocNotQualified.optionItems.map((item) => (
                    <BodyBaseText numberOfLines={0} key={item}>
                      {item}
                    </BodyBaseText>
                  ))}
                />
              </View>
            ) : null}

            {vlocNotQualified?.footer ? (
              <BodyBaseText style={styles.enhancedStyle} numberOfLines={0}>
                {vlocNotQualified.footer}
              </BodyBaseText>
            ) : null}

            {vlocNoBelieve ? (
              <>
                {vlocNoBelieve.title ? (
                  <TitleMdText
                    style={styles.footerTitleStyle}
                    numberOfLines={0}
                  >
                    {vlocNoBelieve.title}
                  </TitleMdText>
                ) : null}
                {vlocNoBelieve.subTitle ? (
                  <BodyBaseBoldText
                    style={styles.defaultStyle}
                    numberOfLines={0}
                  >
                    {vlocNoBelieve.subTitle}
                  </BodyBaseBoldText>
                ) : null}
                {vlocNoBelieve.description ? (
                  <BodyBaseText style={styles.defaultStyle} numberOfLines={0}>
                    {vlocNoBelieve.description}
                  </BodyBaseText>
                ) : null}
                {vlocNoBelieve.articleLink?.copy ? (
                  <HyperlinkBaseText
                    style={styles.zendeskLinkStyle}
                    numberOfLines={0}
                    onPress={showZendesk}
                  >
                    {vlocNoBelieve.articleLink.copy}
                  </HyperlinkBaseText>
                ) : null}
              </>
            ) : null}
          </View>
        </ScrollView>
        {vlocNoBelieve ? (
          <View style={styles.divider}>
            <HairLineDivider />
          </View>
        ) : null}
        <View style={styles.btnStyle}>
          {vlocNoBelieve ? (
            <Button
              label={translatedText('notQualified.buttons.openBelieve')}
              onPress={goBelieve}
              type="PRIMARY"
              analyticsId="believeFlowInitiated"
            />
          ) : (
            <Button
              label={translatedText('notQualified.buttons.done')}
              onPress={goHome}
              type="PRIMARY"
            />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  btnStyle: {
    marginBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  defaultStyle: {
    marginTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  divider: {
    marginBottom: theme.spacing.m,
  },
  enhancedStyle: {
    marginTop: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
  },
  footerTitleStyle: {
    marginTop: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  mainContent: {
    flex: 1,
  },
  scrollViewContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.m,
  },
  zendeskLinkStyle: {
    marginVertical: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default NotQualifiedScreen;
